import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ACCOUNT_TYPE_LABELS_FA,
  faNum as fa,
  formatRial,
  type AccountType,
  type GlAccount,
  type JournalEntryDto,
  type TrialBalance,
} from '@kia/shared';
import { api } from '../api/client';
import { useApiResource } from '../lib/useApiResource';
import {
  Button,
  Card,
  Chip,
  ErrorRetry,
  Field,
  Input,
  Kpi,
  NumberInput,
  useConfirm,
  useToast,
} from '../ui';
import { TaxInvoicePanel } from './TaxInvoicePanel';
import { SepidarBridgePanel } from './SepidarBridgePanel';
import { AgingPanel } from './AgingPanel';
import { BankReconPanel } from './BankReconPanel';
import { InventoryPanel } from './InventoryPanel';
import { WorkOrderPanel } from './WorkOrderPanel';
import { PayrollPanel } from './PayrollPanel';
import { AssetsPanel } from './AssetsPanel';
import { StatementsPanel } from './StatementsPanel';
import { ReconciliationPanel } from './ReconciliationPanel';

/**
 * دفترداری — the double-entry ledger (LEAVE_SEPIDAR_PLAN.md Phase 1/1b). Finance sees the trial balance,
 * posts manual journal entries (with a live balance guard), reverses mistakes, browses the chart, and
 * runs the auto-posting sync that derives the books from operations. Finance/Admin only.
 */

interface LineDraft {
  accountCode: string;
  debit: number;
  credit: number;
}

const TYPE_TONE: Record<AccountType, 'neutral' | 'info' | 'success' | 'warn' | 'danger'> = {
  asset: 'info',
  liability: 'warn',
  equity: 'neutral',
  revenue: 'success',
  expense: 'danger',
};

export function AccountingScreen({
  focusInvoiceDeal = null,
  onInvoiceFocused,
}: {
  /** deep-link from a delivered deal (E13-10): scroll to + prefill the tax-invoice panel. */
  focusInvoiceDeal?: number | null;
  onInvoiceFocused?: () => void;
} = {}) {
  const toast = useToast();
  const confirm = useConfirm();
  const invoiceRef = useRef<HTMLDivElement>(null);
  const journalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusInvoiceDeal != null)
      invoiceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusInvoiceDeal]);
  const accountsRes = useApiResource<{ accounts: GlAccount[] }>('/api/accounting/accounts');
  const entriesRes = useApiResource<{ entries: JournalEntryDto[] }>('/api/accounting/entries');
  const tbRes = useApiResource<TrialBalance>('/api/accounting/trial-balance');
  const accounts = accountsRes.data?.accounts ?? [];
  const entries = entriesRes.data?.entries ?? [];
  const tb = tbRes.data;
  const [showChart, setShowChart] = useState(false);
  const [busy, setBusy] = useState(false);

  // New-entry form state.
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([
    { accountCode: '', debit: 0, credit: 0 },
    { accountCode: '', debit: 0, credit: 0 },
  ]);

  const postable = useMemo(() => accounts.filter((a) => a.postable && a.active), [accounts]);

  const refresh = () => {
    accountsRes.reload();
    entriesRes.reload();
    tbRes.reload();
  };

  const totalDebit = lines.reduce((s, l) => s + (l.debit || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (l.credit || 0), 0);
  const balanced = totalDebit === totalCredit && totalDebit > 0;

  function setLine(i: number, patch: Partial<LineDraft>) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((ls) => [...ls, { accountCode: '', debit: 0, credit: 0 }]);
  }
  function removeLine(i: number) {
    setLines((ls) => (ls.length > 2 ? ls.filter((_, idx) => idx !== i) : ls));
  }

  /**
   * TKT-10a — draft a journal from an unmatched bank-statement line (bank activity missing from the book:
   * a fee, a direct withdrawal, a deposit). Fills the BANK leg (1002) on the correct side and leaves the
   * counter-account blank for the user to pick, then scrolls to the entry form. The user still reviews +
   * posts — same balanced-entry guard as any manual journal.
   */
  function draftFromBankLine(s: { date: string; amount: number; desc: string }) {
    const BANK = '1002'; // ACC.BANK — the seeded bank account code
    const amt = Math.abs(Math.round(s.amount));
    if (/^\d{4}-\d{2}-\d{2}$/.test(s.date)) setDate(s.date);
    setMemo(s.desc || 'مغایرت بانکی');
    setLines(
      s.amount >= 0
        ? [
            { accountCode: BANK, debit: amt, credit: 0 },
            { accountCode: '', debit: 0, credit: amt },
          ]
        : [
            { accountCode: BANK, debit: 0, credit: amt },
            { accountCode: '', debit: amt, credit: 0 },
          ],
    );
    journalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function postEntry() {
    if (!balanced) {
      toast('سند تراز نیست — جمع بدهکار و بستانکار باید برابر باشد', 'error');
      return;
    }
    const payload = {
      date,
      memo,
      source: 'manual',
      lines: lines
        .filter((l) => l.accountCode && (l.debit > 0 || l.credit > 0))
        .map((l) => ({ accountCode: l.accountCode, debit: l.debit, credit: l.credit })),
    };
    setBusy(true);
    const r = await api('/api/accounting/entries', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    setBusy(false);
    if (!r.ok) {
      toast((r.body as { message?: string }).message ?? 'خطا در ثبت سند', 'error');
      return;
    }
    toast('سند ثبت شد ✓');
    setMemo('');
    setLines([
      { accountCode: '', debit: 0, credit: 0 },
      { accountCode: '', debit: 0, credit: 0 },
    ]);
    refresh();
  }

  async function reverse(id: number) {
    if (
      !(await confirm({
        title: 'برگشت سند',
        message: 'یک سند برگشتیِ معکوس در دفترکل ثبت می‌شود. این کار قابل بازگشت نیست. ادامه؟',
        danger: true,
      }))
    )
      return;
    const r = await api(`/api/accounting/entries/${id}/reverse`, { method: 'POST' });
    if (!r.ok) {
      toast((r.body as { message?: string }).message ?? 'خطا در برگشت سند', 'error');
      return;
    }
    toast('سند برگشت خورد ✓');
    refresh();
  }

  const loadError =
    accountsRes.status === 'error'
      ? accountsRes.error
      : entriesRes.status === 'error'
        ? entriesRes.error
        : tbRes.status === 'error'
          ? tbRes.error
          : null;

  return (
    <div className="stack" style={{ display: 'grid', gap: 16 }}>
      {loadError ? <ErrorRetry error={loadError} onRetry={refresh} /> : null}
      {/* Honest status: revenue is recognized by tax invoices, which are not wired to the GL yet. */}
      <div
        style={{
          borderInlineStart: '4px solid var(--amber)',
          background: 'var(--amber-bg)',
          borderRadius: 'var(--radius-xs)',
          padding: '10px 14px',
          lineHeight: 1.9,
        }}
      >
        <b>یادداشت:</b> شناخت درآمد از طریق «فاکتور مالیاتی» انجام و به‌صورت خودکار به دفترکل ثبت
        می‌شود (بدهکار: دریافتنی، بستانکار: فروش + مالیات بر ارزش افزوده). ارسال فاکتور فعلاً با
        درگاه آزمایشی انجام می‌شود؛ برای ارسال واقعی به سامانهٔ مؤدیان، «معتمد» یا گواهی مالیاتی
        باید پیکربندی شود.
      </div>

      {/* Trial balance — the integrity headline. */}
      <Card
        title={
          <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
            تراز آزمایشی
            {tb ? (
              <Chip tone={tb.balanced ? 'success' : 'danger'}>
                {tb.balanced ? 'تراز ✓' : 'ناتراز ✗'}
              </Chip>
            ) : null}
          </span>
        }
      >
        {tb ? (
          <>
            <div className="kpi-grid" style={{ marginBottom: 12 }}>
              <Kpi label="جمع بدهکار" value={formatRial(tb.totalDebit)} />
              <Kpi label="جمع بستانکار" value={formatRial(tb.totalCredit)} />
              <Kpi
                label="اختلاف"
                value={formatRial(tb.totalDebit - tb.totalCredit)}
                tone={tb.balanced ? 'good' : 'bad'}
              />
            </div>
            {tb.rows.length > 0 ? (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>کد</th>
                      <th>حساب</th>
                      <th>نوع</th>
                      <th>بدهکار</th>
                      <th>بستانکار</th>
                      <th>مانده</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tb.rows.map((r) => (
                      <tr key={r.code}>
                        <td style={{ fontVariantNumeric: 'tabular-nums' }}>{r.code}</td>
                        <td>{r.name}</td>
                        <td>
                          <Chip tone={TYPE_TONE[r.type]}>{ACCOUNT_TYPE_LABELS_FA[r.type]}</Chip>
                        </td>
                        <td>{r.debit ? formatRial(r.debit) : '—'}</td>
                        <td>{r.credit ? formatRial(r.credit) : '—'}</td>
                        <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatRial(r.balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: 'var(--ink-3)' }}>هنوز سندی ثبت نشده است.</p>
            )}
          </>
        ) : (
          <p style={{ color: 'var(--ink-3)' }}>در حال بارگذاری…</p>
        )}
      </Card>

      {/* Financial statements + VAT return — the capstone reports. */}
      <StatementsPanel />

      {/* Reconciliation gate — is the ledger a faithful projection of operations? Folds in the
          auto-posting sync (post missing / reverse stale) + the subledger tie-out worksheet. */}
      <ReconciliationPanel onSynced={refresh} />

      {/* Tax invoices (مودیان) — issuing one recognizes revenue in the ledger. */}
      <div ref={invoiceRef}>
        <TaxInvoicePanel focusDealId={focusInvoiceDeal} onFocused={onInvoiceFocused} />
      </div>

      {/* Manual journal entry. */}
      <div ref={journalRef}>
        <Card title="ثبت سند دستی">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <Field label="تاریخ">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label="شرح سند">
              <Input
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="مثلاً: تنخواه"
              />
            </Field>
          </div>

          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '45%' }}>حساب</th>
                <th>بدهکار</th>
                <th>بستانکار</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    <select
                      className="input"
                      value={l.accountCode}
                      onChange={(e) => setLine(i, { accountCode: e.target.value })}
                    >
                      <option value="">— انتخاب حساب —</option>
                      {postable.map((a) => (
                        <option key={a.code} value={a.code}>
                          {a.code} — {a.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <NumberInput
                      value={l.debit}
                      onValue={(v) => setLine(i, { debit: v, credit: 0 })}
                    />
                  </td>
                  <td>
                    <NumberInput
                      value={l.credit}
                      onValue={(v) => setLine(i, { credit: v, debit: 0 })}
                    />
                  </td>
                  <td>
                    <Button
                      variant="secondary"
                      onClick={() => removeLine(i)}
                      disabled={lines.length <= 2}
                    >
                      حذف
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>
                  <Button variant="secondary" onClick={addLine}>
                    + ردیف
                  </Button>
                </td>
                <td style={{ fontVariantNumeric: 'tabular-nums' }}>{formatRial(totalDebit)}</td>
                <td style={{ fontVariantNumeric: 'tabular-nums' }}>{formatRial(totalCredit)}</td>
                <td>
                  <Chip tone={balanced ? 'success' : 'neutral'}>
                    {balanced ? 'تراز ✓' : `اختلاف ${formatRial(totalDebit - totalCredit)}`}
                  </Chip>
                </td>
              </tr>
            </tfoot>
          </table>

          <div style={{ marginTop: 12 }}>
            <Button onClick={() => void postEntry()} disabled={busy || !balanced}>
              {busy ? 'در حال ثبت…' : 'ثبت سند'}
            </Button>
          </div>
        </Card>
      </div>

      {/* Recent journal entries. */}
      {entries.length > 0 ? (
        <Card title="اسناد اخیر">
          <table className="table">
            <thead>
              <tr>
                <th>شماره</th>
                <th>تاریخ</th>
                <th>شرح</th>
                <th>منبع</th>
                <th>مبلغ</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{e.no}</td>
                  <td>{e.date}</td>
                  <td>{e.memo || '—'}</td>
                  <td>
                    <Chip
                      tone={
                        e.source === 'manual'
                          ? 'neutral'
                          : e.source === 'reversal'
                            ? 'warn'
                            : 'info'
                      }
                    >
                      {e.source}
                    </Chip>
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{formatRial(e.totalDebit)}</td>
                  <td>
                    {e.reversedBy || e.reversalOf ? (
                      <span style={{ color: 'var(--ink-3)' }}>
                        {e.reversalOf ? 'سندِ برگشت' : 'برگشت‌خورده'}
                      </span>
                    ) : (
                      <Button variant="secondary" onClick={() => void reverse(e.id)}>
                        برگشت
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}

      {/* AR/AP aging. */}
      <AgingPanel />

      {/* Bank reconciliation. */}
      <BankReconPanel onDraft={draftFromBankLine} />

      {/* Materials inventory valuation. */}
      <InventoryPanel />

      {/* Production work orders. */}
      <WorkOrderPanel onCompleted={refresh} />

      {/* Payroll. */}
      <PayrollPanel onPosted={refresh} />

      {/* Fixed assets + depreciation. */}
      <AssetsPanel onPosted={refresh} />

      {/* Sepidar bridge — opening balances in, reconciliation exports out. */}
      <SepidarBridgePanel onPosted={refresh} />

      {/* Chart of accounts (collapsible). */}
      <Card title="کدینگ حساب‌ها">
        <Button variant="secondary" onClick={() => setShowChart((s) => !s)}>
          {showChart ? 'بستن' : `نمایش ${fa(accounts.length)} حساب`}
        </Button>
        {showChart ? (
          <table className="table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>کد</th>
                <th>نام</th>
                <th>نوع</th>
                <th>قابل ثبت</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.code}>
                  <td
                    style={{
                      fontVariantNumeric: 'tabular-nums',
                      paddingInlineStart: a.parentCode ? 24 : 0,
                    }}
                  >
                    {a.code}
                  </td>
                  <td>{a.name}</td>
                  <td>
                    <Chip tone={TYPE_TONE[a.type]}>{ACCOUNT_TYPE_LABELS_FA[a.type]}</Chip>
                  </td>
                  <td>{a.postable ? '✓' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Card>
    </div>
  );
}
